import { useMsal } from "@azure/msal-react";
import { useMemo } from "react";

interface UserPositionResult {
  userPosition: string | null;
  userName: string | null;
  isAvailable: boolean;
}

/**
 * Custom hook to get the user's assigned position based on their name.
 * Position is derived from the first 4 letters of the user's name (capitalized).
 *
 * Example: "Peter" → "PETE", "John Smith" → "JOHN"
 */
export const useUserPosition = (): UserPositionResult => {
  const { accounts } = useMsal();

  return useMemo(() => {
    if (!accounts || accounts.length === 0) {
      return {
        userPosition: null,
        userName: null,
        isAvailable: false,
      };
    }

    const account = accounts[0];
    const displayName = account?.name || account?.username || "";

    if (!displayName || displayName.trim().length === 0) {
      return {
        userPosition: null,
        userName: null,
        isAvailable: false,
      };
    }

    // Extract first name (before any space) and take first 4 characters
    const firstName = displayName.trim().split(/\s+/)[0];
    const positionCode = firstName.substring(0, 4).toUpperCase();

    return {
      userPosition: positionCode,
      userName: displayName,
      isAvailable: positionCode.length > 0,
    };
  }, [accounts]);
};
